import type { Map as MapboxMap } from 'mapbox-gl'

import { SitePermitStatus } from '~/client/graph'
import {
  ASSIGNED_STAR_FILL,
  getWorkflowStatusPinFill,
} from '~/client/src/shared/constants/workflowStatusColors'
import {
  getWorkflowTypeGlyph,
  resolveGlyphName,
} from '~/client/src/shared/constants/workflowTypeGlyphs'
import {
  ASSIGNED_STAR_SPEC,
  MAP_SHADOW_REACH_PX,
  PIN_PATH_DATA,
  PIN_SPEC,
} from '~/client/src/shared/map/markerGeometry'

import { deliveryPinColors as deliveryLadderColors } from '../../Konva/deliveryPinColors'
import {
  compositeMapShadow,
  DEFAULT_GLYPH_STROKE_WIDTH,
  iconNodesToPath2Ds,
  rasterRatio,
} from './iconGeometry'

import Colors from '~/client/src/shared/theme.module.scss'

export const WORKFLOW_PIN_KEY_PREFIX = 'wfpin-'
// Aggregated (count-bubble) pin token; workflow icon names never contain '='.
const COUNT_TOKEN = '=count'
// The count flag and the glyph both live in the token, so a counted pin keeps its own icon
// instead of every cluster sharing one image. Whether a mixed group *should* show a specific
// icon is the caller's call — it is the only side that knows what the group holds.
const COUNT_SEPARATOR = '.'

// Read off PIN_SPEC rather than restated here: the DOM pin draws the same silhouette at the same
// size, and two copies of these numbers is how a raster and a component drift apart.
const PIN_VIEWBOX_W = PIN_SPEC.viewBoxWidth
const PIN_TIP_Y = PIN_SPEC.tipY
const PIN_BOX_PX = PIN_SPEC.boxPx
const HOVERED_PIN_BOX_PX = PIN_SPEC.hoveredBoxPx
const PIN_VIEWBOX_H = PIN_SPEC.viewBoxHeight
const PIN_STROKE_PX = PIN_SPEC.strokePx
const PIN_GLYPH_CENTER_Y = PIN_SPEC.glyphCenterY
const ICON_BOX_PX = PIN_SPEC.boxPx * PIN_SPEC.glyphRatio
// Every workflow mark is authored on the 24 grid.
const GLYPH_GRID = 24
const COUNT_BUBBLE_PX = 24
// NEGATIVE, and load-bearing: the bubble's outline overlaps the pin's, which merges the two
// into a single silhouette — a pin wearing a badge. Held a couple of pixels apart, two discs
// of one colour with a white ring each read as two bubbles instead, and the head's own ring
// plus the glyph's make three of them stacked.
const COUNT_BUBBLE_GAP_PX = -1
const COUNT_BUBBLE_OUTER_PX = COUNT_BUBBLE_PX + PIN_STROKE_PX * 2

// Pin tip lands exactly at the bottom edge (icon-anchor: 'bottom'). The path is inset for
// its own stroke, so the viewBox already contains it and the canvas needs no bleed padding.
const pinScale = (boxPx: number) => boxPx / PIN_VIEWBOX_H
const pinH = (boxPx: number) => PIN_TIP_Y * pinScale(boxPx)
const pinCanvasW = (boxPx: number) => Math.ceil(PIN_VIEWBOX_W * pinScale(boxPx))
const iconCenterBelowPinTop = (boxPx: number) =>
  PIN_GLYPH_CENTER_Y * pinScale(boxPx)

// Tip-to-bubble-center distance for the counts layer's text-offset —
// data-driven because the hovered pin is taller.
export const COUNT_BUBBLE_CENTER_ABOVE_TIP_PX =
  pinH(PIN_BOX_PX) + COUNT_BUBBLE_GAP_PX + COUNT_BUBBLE_OUTER_PX / 2
export const COUNT_BUBBLE_CENTER_ABOVE_TIP_HOVERED_PX =
  pinH(HOVERED_PIN_BOX_PX) + COUNT_BUBBLE_GAP_PX + COUNT_BUBBLE_OUTER_PX / 2

// Popup clearance derived from the art so resizing the pin moves the popup;
// counted pins reach higher (bubble above the head).
export const POPUP_CLEARANCE_GAP_PX = 8
export const PIN_POPUP_CLEARANCE_PX =
  Math.round(pinH(HOVERED_PIN_BOX_PX)) + POPUP_CLEARANCE_GAP_PX
export const COUNTED_PIN_POPUP_CLEARANCE_PX =
  Math.round(
    COUNT_BUBBLE_CENTER_ABOVE_TIP_HOVERED_PX + COUNT_BUBBLE_OUTER_PX / 2,
  ) + POPUP_CLEARANCE_GAP_PX

// Two palettes share this rasterizer: SitePermitStatus for workflows, the older boolean ladder
// for deliveries. isDelivery picks the branch explicitly, so a workflow pin with no status can
// never fall through to the delivery colours.
export interface IWorkflowPinVisualState {
  iconName: string
  withCount: boolean
  isSelected: boolean
  isAssigned: boolean
  // The DOM hover-grow (48→50px pin, icon size unchanged) as an image state.
  isHovered?: boolean
  // Shared in from another project: it carries a status, but not one this project acts on.
  isShared?: boolean

  // Workflow pins.
  status?: SitePermitStatus
  isLate?: boolean
  // A cluster whose members disagree — the only case that renders black.
  isMixed?: boolean

  // Delivery pins.
  isDelivery?: boolean
  isDone?: boolean
  isCanceled?: boolean
}

// Literal hex only — a raster cannot read a CSS variable. Memoized: this runs four times per
// pin feature and features rebuild on every pointer sample of a hover. No theme in the key
// because both branches resolve at build time; make a status theme-reactive and that changes.
const colorsByState = new Map<string, { fill: string; icon: string }>()

// The delivery palette, unchanged: DeliveryStatus is a separate enum with its own colours
// and is deliberately not part of the workflow status schema.
// The whiteboard pin's ladder, renamed to this module's key. isShared never reaches it: a
// shared pin carries no isDelivery, so it resolves through the workflow branch's closed grey.
function deliveryPinColors(state: IWorkflowPinVisualState): {
  fill: string
  icon: string
} {
  const { fill, ink } = deliveryLadderColors(state)
  return { fill, icon: ink }
}

export function getWorkflowPinColors(state: IWorkflowPinVisualState): {
  fill: string
  icon: string
} {
  const {
    isDelivery,
    isSelected,
    isDone,
    isAssigned,
    isCanceled,
    isShared,
    status,
    isLate,
    isMixed,
  } = state
  const stateKey = isDelivery
    ? `d${+!!isSelected}${+!!isDone}${+!!isAssigned}${+!!isCanceled}${+!!isShared}`
    : `w${status ?? ''}|${+!!isLate}${+!!isSelected}${+!!isShared}${+!!isMixed}`
  const cached = colorsByState.get(stateKey)
  if (cached) {
    return cached
  }
  const colors = isDelivery
    ? deliveryPinColors(state)
    : {
        fill: getWorkflowStatusPinFill({
          status,
          isLate,
          isSelected,
          isShared,
          isMixed,
        }),
        // The whole pin body carries the status, so the stroke and glyph are always white.
        icon: Colors.mapWhite,
      }
  colorsByState.set(stateKey, colors)
  return colors
}

// The enlarged art. Hover borrows it for as long as the pointer is there; a selected pin
// KEEPS it, because being the picked pin is a state, not a moment — it must not shrink back
// the instant the pointer leaves.
const ENLARGED_SEGMENT = 'big'
// 'assigned to me'. Appended BEFORE the size flag so an enlarged key still ends in 'big',
// which is what the layer's own key assertions and the sibling pre-warm slice on.
const ASSIGNED_SEGMENT = 'me'

// State-encoded image id (cf. getMarkerImageKey): everything the renderer needs
// is parseable back out, so images register lazily via styleimagemissing.
export function getWorkflowPinImageKey(state: IWorkflowPinVisualState): string {
  const { fill, icon } = getWorkflowPinColors(state)
  const glyphName = resolveGlyphName(state.iconName)
  const iconToken = state.withCount
    ? `${COUNT_TOKEN}${COUNT_SEPARATOR}${glyphName}`
    : glyphName
  const base = `${WORKFLOW_PIN_KEY_PREFIX}${iconToken}-${fill.replace(
    '#',
    '',
  )}-${icon.replace('#', '')}`
  // The star is baked art, so it has to be part of the identity: without it an assigned
  // and an unassigned pin share one image and the star appears or vanishes depending on
  // which rasterized first. Delivery pins say "assigned" through their fill instead.
  const withStar = state.isAssigned && !state.isDelivery
  const assigned = withStar ? `${base}-${ASSIGNED_SEGMENT}` : base
  return state.isHovered || state.isSelected
    ? `${assigned}-${ENLARGED_SEGMENT}`
    : assigned
}

// Straight onto the canvas from the registry's geometry. The old svg-blob round-trip was the
// only await in the raster and measured pixel-identical at dpr 2. Every mark is stroke-only on
// the 24 grid, so the fit is a constant scale with no filled branch to pick.
function drawIconGlyph(
  ctx: CanvasRenderingContext2D,
  iconName: string,
  color: string,
  centerX: number,
  centerY: number,
  sizePx: number,
): void {
  const scale = sizePx / GLYPH_GRID

  ctx.save()
  ctx.translate(centerX - sizePx / 2, centerY - sizePx / 2)
  ctx.scale(scale, scale)
  // Set inside the scaled context on purpose: a stroke weight means grid units here, the same
  // way the svg's stroke-width did.
  ctx.strokeStyle = color
  ctx.lineWidth = DEFAULT_GLYPH_STROKE_WIDTH
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  iconNodesToPath2Ds(getWorkflowTypeGlyph(iconName)).forEach(path =>
    ctx.stroke(path),
  )
  ctx.restore()
}

function renderWorkflowPinImage(
  iconToken: string,
  fillColor: string,
  iconColor: string,
  isEnlarged: boolean,
  withStar: boolean,
  ratio: number,
): ImageData | null {
  const withCount = iconToken.startsWith(`${COUNT_TOKEN}${COUNT_SEPARATOR}`)
  const glyphName = withCount
    ? iconToken.slice(COUNT_TOKEN.length + COUNT_SEPARATOR.length)
    : iconToken
  const boxPx = isEnlarged ? HOVERED_PIN_BOX_PX : PIN_BOX_PX
  const scale = pinScale(boxPx)
  const pinHeight = pinH(boxPx)
  const starSideForWidth = withStar
    ? ASSIGNED_STAR_SPEC.overhangRight * pinScale(boxPx)
    : 0
  const w = Math.round(
    (Math.max(withCount ? COUNT_BUBBLE_OUTER_PX : 0, pinCanvasW(boxPx)) +
      starSideForWidth * 2 +
      MAP_SHADOW_REACH_PX.side * 2) *
      ratio,
  )
  // No stroke headroom: the path is already inset for its own stroke, and adding more sheared
  // the rounded cap off the tip. Grows symmetrically sideways so the anchor stays on the
  // horizontal centre, and only upward, so the bottom edge is still the tip.
  const starTopPx = withStar ? ASSIGNED_STAR_SPEC.overhangTop * scale : 0
  const h = Math.round(
    ((withCount
      ? pinHeight + COUNT_BUBBLE_GAP_PX + COUNT_BUBBLE_OUTER_PX
      : pinHeight) +
      starTopPx +
      MAP_SHADOW_REACH_PX.top) *
      ratio,
  )

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  // Off-screen first: the shadow belongs to the whole group, so it needs a finished silhouette
  // and exactly one composite — drawn per pass, the antialiased edges stack.
  const art = document.createElement('canvas')
  art.width = w
  art.height = h
  const artCtx = art.getContext('2d')
  if (!artCtx) return null

  const centerX = w / 2
  // Pin tip sits exactly at the bottom edge in every variant.
  const pinTop = h - pinHeight * ratio
  // Pin viewBox coordinates -> canvas, so the overlays place off the same numbers Figma
  // reports rather than off the canvas edges, which move with the padding.
  const vbX = (x: number): number =>
    centerX + (x - PIN_VIEWBOX_W / 2) * scale * ratio
  const vbY = (y: number): number => pinTop + y * scale * ratio

  artCtx.save()
  artCtx.translate(centerX - (PIN_VIEWBOX_W / 2) * scale * ratio, pinTop)
  artCtx.scale(scale * ratio, scale * ratio)
  const pin = new Path2D(PIN_PATH_DATA)
  artCtx.fillStyle = fillColor
  artCtx.fill(pin, 'evenodd')
  artCtx.lineWidth = PIN_STROKE_PX / scale
  artCtx.strokeStyle = iconColor
  artCtx.stroke(pin)
  artCtx.restore()

  const iconSize = ICON_BOX_PX * ratio
  const iconCenterY = pinTop + iconCenterBelowPinTop(boxPx) * ratio
  drawIconGlyph(artCtx, glyphName, iconColor, centerX, iconCenterY, iconSize)

  if (withStar) {
    // 1:1 in pin viewBox units, so the placement is Figma's own numbers. lineWidth is
    // pre-divided by the scale for the same reason the pin's is: the stroke means CSS
    // pixels, not viewBox units.
    artCtx.save()
    artCtx.translate(vbX(ASSIGNED_STAR_SPEC.x), vbY(ASSIGNED_STAR_SPEC.y))
    artCtx.scale(scale * ratio, scale * ratio)
    const star = new Path2D(ASSIGNED_STAR_SPEC.path)
    artCtx.fillStyle = ASSIGNED_STAR_FILL
    artCtx.fill(star)
    artCtx.lineWidth = ASSIGNED_STAR_SPEC.strokePx / scale
    artCtx.strokeStyle = Colors.mapWhite
    artCtx.stroke(star)
    artCtx.restore()
  }

  if (withCount) {
    // Drawn last so it sits over the star, as it does in Figma. The number itself is GL
    // text (the counts layer's text-offset), not baked in.
    const r = (COUNT_BUBBLE_OUTER_PX / 2) * ratio
    const cy = pinTop - COUNT_BUBBLE_GAP_PX * ratio - r
    artCtx.beginPath()
    artCtx.arc(centerX, cy, r - ratio / 2, 0, Math.PI * 2)
    artCtx.fillStyle = fillColor
    artCtx.fill()
    artCtx.lineWidth = PIN_STROKE_PX * ratio
    artCtx.strokeStyle = iconColor
    artCtx.stroke()
  }

  compositeMapShadow(ctx, art, w, h, ratio)

  return ctx.getImageData(0, 0, w, h)
}

// styleimagemissing re-fires every frame while an image stays missing — track
// in-flight and failed renders or it's a per-frame retry loop; retries stay
// bounded so a transient decode blip doesn't blank the pin for the session.
const pendingImageIds = new Set<string>()
const failedRenderAttempts = new Map<string, number>()
const MAX_PIN_RENDER_ATTEMPTS = 3

// `density`: how many times its own size the map draws the pin, so it is rasterized that sharp.
export function registerMissingWorkflowPinImage(
  map: MapboxMap,
  imageId: string,
  density = 1,
): void {
  if (!imageId.startsWith(WORKFLOW_PIN_KEY_PREFIX)) return
  if (
    map.hasImage(imageId) ||
    pendingImageIds.has(imageId) ||
    (failedRenderAttempts.get(imageId) ?? 0) >= MAX_PIN_RENDER_ATTEMPTS
  ) {
    return
  }

  // wfpin-<iconToken>-<fillHex>-<iconHex>[-me][-big]; icon names contain dashes, so the
  // fixed segments parse from the end. Flags come off in reverse of the order they were
  // appended — a glyph name can never be last, since the two hexes always follow it.
  const withoutPrefix = imageId.slice(WORKFLOW_PIN_KEY_PREFIX.length)
  const parts = withoutPrefix.split('-')
  const isEnlarged = parts[parts.length - 1] === ENLARGED_SEGMENT
  if (isEnlarged) parts.pop()
  const withStar = parts[parts.length - 1] === ASSIGNED_SEGMENT
  if (withStar) parts.pop()
  if (parts.length < 3) return
  const iconHex = '#' + parts.pop()
  const fillHex = '#' + parts.pop()
  const iconToken = parts.join('-')

  const recordFailure = (): void => {
    failedRenderAttempts.set(
      imageId,
      (failedRenderAttempts.get(imageId) ?? 0) + 1,
    )
  }

  // pendingImageIds still guards the pre-warm recursion below, which re-enters this function
  // before the id it is rendering has been added to the map.
  pendingImageIds.add(imageId)
  try {
    const ratio = rasterRatio(density)
    const imageData = renderWorkflowPinImage(
      iconToken,
      fillHex,
      iconHex,
      isEnlarged,
      withStar,
      ratio,
    )
    if (!imageData) {
      recordFailure()
      return
    }
    try {
      map.addImage(imageId, imageData, { pixelRatio: ratio })
      map.triggerRepaint()
    } catch {
      // The style is mid-reload (the render landed across a basemap switch);
      // the fresh style re-fires styleimagemissing, so don't burn an attempt.
      return
    }
    // Pre-warm the size sibling: the base⇄hov swap on first hover would
    // otherwise hit a missing image and blink the pin for a few frames.
    const sibling = isEnlarged
      ? imageId.slice(0, -(ENLARGED_SEGMENT.length + 1))
      : `${imageId}-${ENLARGED_SEGMENT}`
    registerMissingWorkflowPinImage(map, sibling, density)
    // Pre-warm the selected (brandBlue) sibling too: missing on the first tap, the
    // placed symbol keeps its unselected image until a later re-placement. Always the
    // enlarged variant — a selected pin is enlarged whether or not it is hovered. The
    // status is not needed: selection outranks it, so every status resolves to the
    // same selected art.
    const selected = getWorkflowPinColors({
      iconName: iconToken,
      withCount: false,
      isSelected: true,
      isAssigned: false,
    })
    const selectedKey = `${WORKFLOW_PIN_KEY_PREFIX}${iconToken}-${selected.fill.replace(
      '#',
      '',
    )}-${selected.icon.replace('#', '')}${
      withStar ? `-${ASSIGNED_SEGMENT}` : ''
    }-${ENLARGED_SEGMENT}`
    if (imageId !== selectedKey) {
      registerMissingWorkflowPinImage(map, selectedKey, density)
    }
  } catch {
    recordFailure()
  } finally {
    pendingImageIds.delete(imageId)
  }
}
